'use client';

import Link from 'next/link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Card, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Line, LineChart, ResponsiveContainer } from 'recharts';

import { Avatar } from '@/components';
import type { BreakingNewsMarket } from '@/types/breaking.types';
import { formatPercentage } from '@/utils';

import { ROUTES } from '@/constants';

interface BreakingMarketItemProps {
  market: BreakingNewsMarket;
  rank: number;
}

export function BreakingMarketItem({ market, rank }: BreakingMarketItemProps) {
  const theme = useTheme();
  const isPositive = market.percent_change > 0;
  const changeColor = isPositive ? theme.palette.success.main : theme.palette.error.main;

  const ChangeIcon = isPositive ? TrendingUpIcon : TrendingDownIcon;
  const changeLabel = formatPercentage(market.price_change, 0);

  const displayQuestion = market.question || '-';
  const displayPercentage = formatPercentage(market?.current_price ?? 0, 0);
  const avatarSrc = market.image ?? undefined;
  const marketSlug = market.events[0].slug ?? undefined;

  const historyData = market?.history ?? [];

  return (
    <Link href={ROUTES.MARKET_DETAILS(marketSlug)} style={{ textDecoration: 'none' }}>
      <Card
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: (theme) =>
              `0 2px 8px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.3 : 0.08)}`,
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 } }}>
          {/* Rank */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              minWidth: { xs: 24, sm: 32 },
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              textAlign: 'left',
            }}
          >
            {rank}
          </Typography>

          {/* Icon / Image */}
          <Box sx={{ position: 'relative', display: 'flex' }}>
            <Avatar
              src={avatarSrc}
              alt={displayQuestion}
              size={56}
              sx={{
                width: { xs: 44, sm: 56 },
                height: { xs: 44, sm: 56 },
                borderRadius: 2,
              }}
            />
            {market?.closed && (
              <CheckCircleIcon
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  fontSize: 16,
                  color: 'success.main',
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'background.paper',
                }}
              />
            )}
          </Box>

          {/* Question and Percentage */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              fontWeight={500}
              sx={{
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                lineHeight: 1.5,
                color: 'text.primary',
                mb: 0.5,

                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayQuestion}
            </Typography>
            {/* Percentage under question */}
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                color: 'text.secondary',
              }}
            >
              {displayPercentage}
            </Typography>
          </Box>

          {/* Change, Graph, and Arrow */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexShrink: 0 }}
          >
            {/* Change with Icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: { xs: 50, sm: 60 },
              }}
            >
              <ChangeIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: changeColor }} />
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  color: changeColor,
                }}
              >
                {changeLabel}
              </Typography>
            </Box>

            {/* Mini Graph - sparkline using Recharts and market.history */}
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: 60, height: 32 }}>
              {historyData.length > 1 && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={historyData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                    <Line
                      type="monotone"
                      dataKey="p"
                      stroke={changeColor}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>

            {/* Arrow */}
            <ChevronRightIcon
              sx={{
                color: 'text.secondary',
                fontSize: { xs: 20, sm: 24 },
                opacity: 0.6,
              }}
            />
          </Box>
        </Box>
      </Card>
    </Link>
  );
}

export default BreakingMarketItem;
